import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  HIGHLIGHT_SOURCE_TYPES,
  HighlightSourceRef,
  HighlightSourceType,
} from '@/client/types/highlights';
import { useCreateHighlightPinMutation } from '@/client/api/highlights/highlights.mutation';

interface Props {
  etid: number;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

const selectClass =
  'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

/** Create an editorial pin for a card (etid). Source type drives the ref shape. */
export function HighlightPinDialog({ etid, open, onOpenChange }: Props) {
  const create = useCreateHighlightPinMutation();
  const [sourceType, setSourceType] = useState<HighlightSourceType>('entity');
  const [refValue, setRefValue] = useState('');
  const [rank, setRank] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');

  const meta = useMemo(
    () => HIGHLIGHT_SOURCE_TYPES.find((s) => s.value === sourceType)!,
    [sourceType],
  );

  const reset = () => {
    setSourceType('entity');
    setRefValue('');
    setRank('');
    setStartAt('');
    setEndAt('');
  };

  const buildRef = (): HighlightSourceRef | null => {
    if (sourceType === 'search') {
      try {
        return { query: JSON.parse(refValue) };
      } catch {
        toast.error('Search query must be valid JSON');
        return null;
      }
    }
    const n = Number(refValue);
    if (!refValue.trim() || Number.isNaN(n)) {
      toast.error(`Enter a valid ${meta.fieldLabel}`);
      return null;
    }
    if (sourceType === 'entity') return { eid: n };
    if (sourceType === 'collection') return { ecolid: n };
    return { cmsoid: n };
  };

  const handleCreate = async () => {
    const source_ref = buildRef();
    if (!source_ref) return;
    try {
      await create.mutateAsync({
        etid,
        source_type: sourceType,
        source_ref,
        rank: rank.trim() ? Number(rank) : undefined,
        start_at: startAt || undefined,
        end_at: endAt || undefined,
      });
      toast.success('Pin added');
      reset();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to add pin');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Feature something here</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>What do you want to feature?</Label>
            <select
              className={selectClass}
              value={sourceType}
              onChange={(e) => {
                setSourceType(e.target.value as HighlightSourceType);
                setRefValue('');
              }}
            >
              {HIGHLIGHT_SOURCE_TYPES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">{meta.hint}</p>
          </div>

          <div className="space-y-1.5">
            <Label>{meta.fieldLabel}</Label>
            {sourceType === 'search' ? (
              <textarea
                className={`${selectClass} min-h-[120px] font-mono`}
                placeholder='{"bool":{"must":[{"term":{"etid":240}}]}}'
                value={refValue}
                onChange={(e) => setRefValue(e.target.value)}
              />
            ) : (
              <Input
                type="number"
                value={refValue}
                placeholder={`Enter the ${meta.fieldLabel.toLowerCase()}`}
                onChange={(e) => setRefValue(e.target.value)}
              />
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Order</Label>
              <Input type="number" value={rank} placeholder="1st, 2nd…" onChange={(e) => setRank(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Show from</Label>
              <Input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Show until</Label>
              <Input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} />
            </div>
          </div>
          <p className="-mt-1 text-[11px] text-gray-400">
            Leave the dates blank to feature it indefinitely.
          </p>

          <Button onClick={handleCreate} disabled={create.isPending} className="w-full">
            Feature it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
